/**
 * Окружение браузера: то, что страница узнаёт о вас без разрешений.
 *
 * Все значения читаются синхронно и доступны любому сайту. Сервер сравнивает их
 * со страной выходного IP (analysis/clientEnv.ts): расхождение таймзоны или
 * языка с гео — самый дешёвый способ поймать VPN, потому что Intl берёт данные
 * из системы, а не из сети.
 */

import type { ClientEnvResult } from '@vpncheck/shared';

/**
 * @param serverTimeMs время сервера в миллисекундах, если известно. Нужно для
 *   расчёта расхождения часов: сам браузер о нём не знает.
 */
export function collectEnv(serverTimeMs: number | null): ClientEnvResult {
  return {
    timezone: resolvedTimezone(),
    timezoneOffsetMinutes: new Date().getTimezoneOffset(),
    languages: collectLanguages(),
    platform: readPlatform(),
    clockSkewMs: serverTimeMs === null ? null : Date.now() - serverTimeMs,
    hardwareConcurrency:
      typeof navigator.hardwareConcurrency === 'number' ? navigator.hardwareConcurrency : null,
    pluginCount: navigator.plugins ? navigator.plugins.length : null,
  };
}

function resolvedTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    return null;
  }
}

/**
 * Языки в порядке предпочтения. navigator.languages — источник, из которого
 * браузер формирует и Accept-Language, поэтому расхождение между ними на
 * стороне сервера означает вмешательство расширения или антидетект-профиля.
 */
function collectLanguages(): string[] {
  const list = Array.isArray(navigator.languages) ? [...navigator.languages] : [];
  if (list.length === 0 && navigator.language) list.push(navigator.language);
  return list.filter((l): l is string => typeof l === 'string' && l.length > 0);
}

/**
 * Платформа. navigator.platform объявлен устаревшим, но именно его читают
 * антифрод-скрипты, поэтому показываем то же значение. Современный
 * userAgentData доступен не везде и требует асинхронного запроса.
 */
function readPlatform(): string | null {
  const platform = navigator.platform;
  return typeof platform === 'string' && platform ? platform : null;
}
